import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "timeline-section" }
const _hoisted_2 = { class: "config-display" }
const _hoisted_3 = { key: 0 }
const _hoisted_4 = { class: "ml-2" }
const _hoisted_5 = { key: 1 }
const _hoisted_6 = { class: "mt-2" }
const _hoisted_7 = { key: 0 }
const _hoisted_8 = { key: 1 }
const _hoisted_9 = { key: 2 }
const _hoisted_10 = { key: 3 }
const _hoisted_11 = { key: 4 }
const _hoisted_12 = { key: 5 }
const _hoisted_13 = {
  key: 0,
  class: "mt-3"
}
const _hoisted_14 = {
  key: 1,
  class: "mt-3"
}
const _hoisted_15 = {
  key: 0,
  class: "text-caption error-text"
}
const _hoisted_16 = { class: "text-caption mt-1" }
const _hoisted_17 = { class: "d-flex justify-space-between align-center mb-3" }
const _hoisted_18 = { class: "text-subtitle-2 font-weight-bold" }
const _hoisted_19 = { class: "d-flex justify-space-between" }
const _hoisted_20 = { class: "text-caption" }
const _hoisted_21 = {
  key: 0,
  class: "text-caption error-text mt-1"
}

import { computed } from 'vue';
import type { MillisecondRange } from '../types/datetime';
import type { TimeRangeConfig } from './date_time_range_generators';
import { validateRanges, DEFAULT_TOLERANCE, ALL_DAY_TOLERANCE } from './date_time_range_generators';

interface ValidationResult {
  isValid: boolean;
  errors: string[];
  badRanges: Set<MillisecondRange>;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'TimelineVisualization',
  props: {
    ranges: {},
    config: {},
    allowedDates: {}
  },
  setup(__props: any) {

// Generated by Copilot
const props = __props;

const validationResults = computed<ValidationResult | null>(() => {
  if (!props.config) return null;
  
  const validation = validateRanges(props.ranges, props.config);
  
  return {
    isValid: validation.valid,
    errors: validation.errors,
    badRanges: new Set(validation.badRanges || [])
  };
});

function formatDate(date: Date): string {
  return date.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric',
    timeZone: 'UTC'
  });
}

function formatDateTime(timestamp: number): string {
  return new Date(timestamp).toLocaleString(undefined, {
    day: '2-digit',
    weekday: 'short',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'UTC'
  });
}

function formatDuration(ms: number): string {
  const hours = Math.floor(ms / (60 * 60 * 1000));
  const minutes = Math.floor((ms % (60 * 60 * 1000)) / (60 * 1000));
  const days = Math.floor(hours / 24);
  
  if (days > 0) {
    return `${days}d ${hours % 24}h`;
  }
  return `${hours}h ${minutes}m`;
}

function formatTolerance(tolerance: [number, number]): string {
  const [before, after] = tolerance;
  
  // Check if it's all day tolerance
  if (before === ALL_DAY_TOLERANCE[0] && after === ALL_DAY_TOLERANCE[1]) {
    return 'All Day';
  }
  
  // Check if it's default tolerance
  if (before === DEFAULT_TOLERANCE[0] && after === DEFAULT_TOLERANCE[1]) {
    return `±${before}h (default)`;
  }
  
  // Check if it's symmetric
  if (before === after) {
    return `±${before}h`;
  }
  
  // Asymmetric tolerance
  return `-${before}h / +${after}h`;
}

const countNotAllowedRange = computed(() => {
  if (!props.allowedDates) return 0;
  
  const allowedDateSet = new Set(
    props.allowedDates.map(d => d.toISOString().split('T')[0])
  );
  
  let count = 0;
  
  for (const range of props.ranges) {
    const startDateStr = new Date(range.start).toISOString().split('T')[0];
    const endDateStr = new Date(range.end).toISOString().split('T')[0];
    
    if (!allowedDateSet.has(startDateStr) || !allowedDateSet.has(endDateStr)) {
      count += 1;
    }
  }
  
  return count;
});

function getRangeValidationClass(item: MillisecondRange, _index: number): string {
  if (!validationResults.value) return '';
  return validationResults.value.badRanges.has(item) ? 'validation-error' : 'validation-success';
}

function getItemValidationError(item: MillisecondRange, _index: number): string | undefined {
  if (!validationResults.value || !validationResults.value.badRanges.has(item)) return undefined;
  return 'This range does not match the configuration filters';
}

return (_ctx: any,_cache: any) => {
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_divider = _resolveComponent("v-divider")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_virtual_scroll = _resolveComponent("v-virtual-scroll")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (__props.config)
      ? (_openBlock(), _createBlock(_component_v_card, {
          key: 0,
          class: "pa-3 mb-3",
          variant: "outlined",
          color: "info"
        }, {
          default: _withCtx(() => [
            _cache[10] || (_cache[10] = _createElementVNode("div", { class: "text-subtitle-2 font-weight-bold mb-2" }, " Configuration Summary ", -1)),
            _createElementVNode("div", _hoisted_2, [
              (__props.config.type === 'single')
                ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                    _createVNode(_component_v_chip, {
                      size: "small",
                      color: "primary"
                    }, {
                      default: _withCtx(() => [...(_cache[0] || (_cache[0] = [
                        _createTextVNode("Single Date", -1)
                      ]))]),
                      _: 1
                    }),
                    _createElementVNode("span", _hoisted_4, _toDisplayString(formatDate(__props.config.singleDate)), 1)
                  ]))
                : _createCommentVNode("", true),
              (__props.config.type === 'multiple')
                ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                    _createVNode(_component_v_chip, {
                      size: "small",
                      color: "primary"
                    }, {
                      default: _withCtx(() => [...(_cache[1] || (_cache[1] = [
                        _createTextVNode("Multiple Dates", -1)
                      ]))]),
                      _: 1
                    }),
                    _createElementVNode("div", _hoisted_6, [
                      (__props.config.dateRange)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                            _cache[2] || (_cache[2] = _createElementVNode("strong", null, "Date Range:", -1)),
                            _createTextVNode(" " + _toDisplayString(formatDate(__props.config.dateRange.start)) + " - " + _toDisplayString(formatDate(__props.config.dateRange.end)), 1)
                          ]))
                        : _createCommentVNode("", true),
                      (__props.config.years)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                            _cache[3] || (_cache[3] = _createElementVNode("strong", null, "Years:", -1)),
                            _createTextVNode(" " + _toDisplayString(__props.config.years.join(', ')), 1)
                          ]))
                        : _createCommentVNode("", true),
                      (__props.config.months)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                            _cache[4] || (_cache[4] = _createElementVNode("strong", null, "Months:", -1)),
                            _createTextVNode(" " + _toDisplayString(__props.config.months.join(', ')), 1)
                          ]))
                        : _createCommentVNode("", true),
                      (__props.config.weekdays)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                            _cache[5] || (_cache[5] = _createElementVNode("strong", null, "Weekdays:", -1)),
                            _createTextVNode(" " + _toDisplayString(__props.config.weekdays.join(', ')), 1)
                          ]))
                        : _createCommentVNode("", true),
                      (__props.config.times)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                            _cache[6] || (_cache[6] = _createElementVNode("strong", null, "Times:", -1)),
                            _createTextVNode(" " + _toDisplayString(__props.config.times.join(', ')), 1)
                          ]))
                        : _createCommentVNode("", true),
                      (__props.config.toleranceHours)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                            _cache[7] || (_cache[7] = _createElementVNode("strong", null, "Tolerance:", -1)),
                            _createTextVNode(" " + _toDisplayString(formatTolerance(__props.config.toleranceHours)), 1)
                          ]))
                        : _createCommentVNode("", true)
                    ])
                  ]))
                : _createCommentVNode("", true)
            ]),
            (__props.allowedDates)
              ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                  _createVNode(_component_v_divider, { class: "my-2" }),
                  _cache[8] || (_cache[8] = _createElementVNode("div", { class: "text-subtitle-2 font-weight-bold mb-2" }, " Allowed Dates Check ", -1)),
                  _createVNode(_component_v_chip, {
                    color: countNotAllowedRange.value === 0 ? 'success' : 'error',
                    size: "small",
                    class: "mb-2"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(countNotAllowedRange.value === 0 ? '✓ All ranges within allowed dates' : `✗ ${countNotAllowedRange.value} ranges outside allowed dates`), 1)
                    ]),
                    _: 1
                  }, 8, ["color"])
                ]))
              : _createCommentVNode("", true),
            (validationResults.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                  _createVNode(_component_v_divider, { class: "my-2" }),
                  _cache[9] || (_cache[9] = _createElementVNode("div", { class: "text-subtitle-2 font-weight-bold mb-2" }, " Validation Results ", -1)),
                  _createVNode(_component_v_chip, {
                    color: validationResults.value.isValid ? 'success' : 'error',
                    size: "small",
                    class: "mb-2"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(validationResults.value.isValid ? '✓ All ranges match config' : '✗ Validation failed'), 1)
                    ]),
                    _: 1
                  }, 8, ["color"]),
                  (!validationResults.value.isValid)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(validationResults.value.errors, (error) => {
                          return (_openBlock(), _createElementBlock("div", { key: error }, " • " + _toDisplayString(error), 1))
                        }), 128))
                      ]))
                    : _createCommentVNode("", true),
                  _createElementVNode("div", _hoisted_16, " Total ranges: " + _toDisplayString(__props.ranges.length), 1)
                ]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    _createVNode(_component_v_card, {
      class: "pa-3",
      variant: "tonal"
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_17, [
          _createElementVNode("span", _hoisted_18, " Generated Time Ranges (" + _toDisplayString(__props.ranges.length) + ") ", 1)
        ]),
        _createVNode(_component_v_virtual_scroll, {
          items: __props.ranges,
          height: "300",
          "item-height": "80"
        }, {
          default: _withCtx(({ item, index }) => [
            _createElementVNode("div", {
              class: _normalizeClass(["range-item pa-2", getRangeValidationClass(item, index)])
            }, [
              _createElementVNode("div", _hoisted_19, [
                _createElementVNode("span", null, _toDisplayString(formatDateTime(item.start)) + " - " + _toDisplayString(formatDateTime(item.end)), 1),
                _createElementVNode("span", _hoisted_20, _toDisplayString(formatDuration(item.end - item.start)), 1)
              ]),
              (getItemValidationError(item, index))
                ? (_openBlock(), _createElementBlock("div", _hoisted_21, _toDisplayString(getItemValidationError(item, index)), 1))
                : _createCommentVNode("", true)
            ], 2)
          ]),
          _: 1
        }, 8, ["items"])
      ]),
      _: 1
    })
  ]))
}
}

})